import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search, X, Check, ChevronLeft, ChevronRight, AlertCircle, Loader2,
  Zap, ExternalLink, AlertTriangle, Star, Copy
} from 'lucide-react';
import { useCexAccount } from '../context/CexAccountContext';
import { apiService } from '../services/apiService';
import { API_BASE_URL } from '../config/api';
import { SUPPORTED_WALLET_NETWORKS } from '../config/wallet';

// ── DexScreener types ───────────────────────────────────────────────────────
interface DexToken {
  address: string;
  name: string;
  symbol: string;
}

interface DexPair {
  chainId: string;
  dexId: string;
  pairAddress: string;
  baseToken: DexToken;
  quoteToken: DexToken;
  priceUsd: string;
  liquidity?: { usd: number };
  volume?: { h24: number };
  fdv?: number;
  pairCreatedAt?: number;
  url: string;
  // computed
  isDuplicate?: boolean;
}

// ── Chain display helpers ───────────────────────────────────────────────────
const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum', eth: 'Ethereum',
  bsc: 'BNB Chain', bnb: 'BNB Chain',
  polygon: 'Polygon', matic: 'Polygon',
  arbitrum: 'Arbitrum', arb: 'Arbitrum',
  avalanche: 'Avalanche', avax: 'Avalanche',
  base: 'Base',
  optimism: 'Optimism',
  solana: 'Solana', sol: 'Solana',
};

const chainLabel = (id: string) => CHAIN_LABELS[id.toLowerCase()] ?? id.toUpperCase();

const chainIcon = (id: string) =>
  SUPPORTED_WALLET_NETWORKS.find(n => n.value === id.toLowerCase() || n.symbol.toLowerCase() === id.toLowerCase())?.icon;

const formatUsd = (n?: number) => {
  if (!n) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

// ── Wizard steps ────────────────────────────────────────────────────────────
const STEPS = ['Token', 'Trade Details', 'Analysis', 'Review'];

const SETUP_CHOICES = ['Breakout', 'Pullback', 'Reversal', 'Trend Continuation', 'Range Trade', 'News/Event', 'Gem Snipe'];
const INDICATOR_CHOICES = ['EMA Crossover', 'RSI Divergence', 'Volume Spike', 'Order Block', 'Liquidity Grab', 'On-chain Signal'];
const TIMEFRAME_CHOICES = ['1m', '5m', '15m', '1H', '4H', 'Daily', 'Weekly'];

interface AddDexTradeWizardProps {
  onClose: () => void;
  onSuccess: (newTrade?: any) => void;
}

const safeParseFloat = (val: string | number): number => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  const clean = val.toString().replace(/,/g, '').trim();
  return parseFloat(clean) || 0;
};

const AddDexTradeWizard: React.FC<AddDexTradeWizardProps> = ({ onClose, onSuccess }) => {
  const { walletAccounts } = useCexAccount();
  const token = localStorage.getItem('auth_token') || '';

  const [step, setStep] = useState(0);

  // ── Step 0: Token search ───────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<DexPair[]>([]);
  const [caInput, setCaInput] = useState('');
  const [showCaInput, setShowCaInput] = useState(false);
  const [selectedPair, setSelectedPair] = useState<DexPair | null>(null);
  const [searchError, setSearchError] = useState('');
  const debounceRef = useRef<any>(null);

  // ── Step 1: Trade details ──────────────────────────────────────────────
  const [walletAccountId, setWalletAccountId] = useState<number | null>(
    walletAccounts.length > 0 ? walletAccounts[0].id : null
  );
  const [buyDate, setBuyDate] = useState(new Date().toISOString().slice(0, 16));
  const [buyPrice, setBuyPrice] = useState('');
  const [qty, setQty] = useState('');
  const [commission, setCommission] = useState('');
  const [fee, setFee] = useState('');
  const [showSell, setShowSell] = useState(false);
  const [sellDate, setSellDate] = useState(new Date().toISOString().slice(0, 16));
  const [sellPrice, setSellPrice] = useState('');

  // ── Step 2: Analysis ──────────────────────────────────────────────────
  const [analysis, setAnalysis] = useState({
    session: '',
    market_cap: '',
    primary_setup_type: '',
    key_indicators: '',
    timeframe_analysis: '',
    risk_percentage: '',
    pre_trade_confidence: 5,
    emotional_load: 5,
    remarks: '',
  });

  // ── Submission ─────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // ── DexScreener token search ────────────────────────────────────────────
  const searchDex = useCallback(async (q: string) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    setSearchError('');
    try {
      const resp = await fetch(
        `https://api.dexscreener.com/latest/dex/search?q=${encodeURIComponent(q)}`
      );
      const data = await resp.json();
      const pairs: DexPair[] = (data.pairs ?? []).slice(0, 30);

      // Mark duplicates: same base token symbol appears more than once
      const symbolCount: Record<string, number> = {};
      for (const p of pairs) {
        const sym = p.baseToken.symbol.toUpperCase();
        symbolCount[sym] = (symbolCount[sym] ?? 0) + 1;
      }
      const marked = pairs.map(p => ({
        ...p,
        isDuplicate: (symbolCount[p.baseToken.symbol.toUpperCase()] ?? 0) > 1,
      }));

      setSearchResults(marked);
    } catch {
      setSearchError('Failed to search DexScreener. Try again.');
    } finally {
      setSearching(false);
    }
  }, []);

  const searchByCA = useCallback(async (ca: string) => {
    if (ca.length < 10) return;
    setSearching(true);
    setSearchError('');
    try {
      const resp = await fetch(
        `https://api.dexscreener.com/latest/dex/tokens/${ca}`
      );
      const data = await resp.json();
      const pairs: DexPair[] = (data.pairs ?? []).slice(0, 10);
      setSearchResults(pairs.map(p => ({ ...p, isDuplicate: false })));
      if (pairs.length === 0) setSearchError('No pairs found for this contract address.');
    } catch {
      setSearchError('Failed to fetch token data. Check the contract address.');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!showCaInput) {
      debounceRef.current = setTimeout(() => searchDex(query), 350);
    }
  }, [query, showCaInput, searchDex]);

  // ── Toggle analysis multiselect ─────────────────────────────────────────
  const toggleOption = (field: keyof typeof analysis, val: string) => {
    const cur = (analysis[field] as string) || '';
    const arr = cur ? cur.split(',').map(s => s.trim()) : [];
    const next = arr.includes(val) ? arr.filter(s => s !== val) : [...arr, val];
    setAnalysis(prev => ({ ...prev, [field]: next.join(', ') }));
  };

  const isSelected = (field: keyof typeof analysis, val: string) =>
    (analysis[field] as string || '').split(',').map(s => s.trim()).includes(val);

  // ── PnL calc ─────────────────────────────────────────────────────────────
  const calcPnl = () => {
    const bp = safeParseFloat(buyPrice) || 0;
    const sp = safeParseFloat(sellPrice) || 0;
    const q = safeParseFloat(qty) || 0;
    const fees = (safeParseFloat(commission) || 0) + (safeParseFloat(fee) || 0);
    const gross = showSell ? (sp - bp) * q : 0;
    const net = gross - fees;
    const status = !showSell ? 'OPEN' : net > 0 ? 'WIN' : net < 0 ? 'LOSS' : 'BREAK';
    return { gross, net, fees, status, buyValue: bp * q };
  };

  // ── Step validation ───────────────────────────────────────────────────────
  const canNext0 = !!selectedPair;
  const canNext1 = !!buyDate && !!buyPrice && safeParseFloat(buyPrice) > 0 && !!qty && safeParseFloat(qty) > 0 &&
    (!showSell || (!!sellDate && !!sellPrice && safeParseFloat(sellPrice) > 0));

  const handleNext = () => {
    if (step === 0 && canNext0) setStep(1);
    else if (step === 1 && canNext1) setStep(2);
    else if (step === 2) setStep(3);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedPair) return;
    setIsSubmitting(true);
    setError('');
    try {
      const pair = `${selectedPair.baseToken.symbol}/${selectedPair.quoteToken.symbol}`;
      const executions: any[] = [{
        cex_account_id: 0,
        wallet_account_id: walletAccountId ?? 0,
        trade_date: buyDate,
        side: 'BUY',
        price: safeParseFloat(buyPrice),
        qty: safeParseFloat(qty),
        commission: safeParseFloat(commission) || 0,
        fee: safeParseFloat(fee) || 0,
        is_dex: true,
        dex_chain: selectedPair.chainId,
        dex_pair_address: selectedPair.pairAddress,
        dex_token_address: selectedPair.baseToken.address,
        ...analysisPayload(),
      }];

      if (showSell) {
        executions.push({
          cex_account_id: 0,
          wallet_account_id: walletAccountId ?? 0,
          trade_date: sellDate,
          side: 'SELL',
          price: safeParseFloat(sellPrice),
          qty: safeParseFloat(qty),
          commission: 0,
          fee: 0,
          is_dex: true,
          dex_chain: selectedPair.chainId,
          dex_pair_address: selectedPair.pairAddress,
          dex_token_address: selectedPair.baseToken.address,
          ...analysisPayload(),
        });
      }

      const res = await apiService.storeManualTrade({ pair, executions }, token);
      onSuccess(res?.data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save trade');
    } finally {
      setIsSubmitting(false);
    }
  };

  const analysisPayload = () => ({
    session: analysis.session || null,
    market_cap: analysis.market_cap || null,
    primary_setup_type: analysis.primary_setup_type || null,
    key_indicators: analysis.key_indicators || null,
    timeframe_analysis: analysis.timeframe_analysis || null,
    risk_percentage: analysis.risk_percentage ? safeParseFloat(analysis.risk_percentage) : null,
    pre_trade_confidence: analysis.pre_trade_confidence,
    emotional_load: analysis.emotional_load,
    remarks: analysis.remarks || null,
  });

  const pnl = calcPnl();

  // ── DexScreener chart URL ─────────────────────────────────────────────────
  const chartUrl = selectedPair
    ? `https://dexscreener.com/${selectedPair.chainId}/${selectedPair.pairAddress}?embed=1&theme=dark&info=0`
    : null;

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-md z-50" />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-[#0D0D0D] border border-neutral-800 rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

          {/* Header */}
          <div className="px-8 pt-8 pb-5 border-b border-neutral-800/60 bg-gradient-to-r from-[#0D0D0D] to-[#111]">
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-purple-400">DEX Trade</span>
                </div>
                <h2 className="text-2xl font-black text-white tracking-tight">+ DEX Trade</h2>
                <p className="text-xs text-neutral-500 mt-0.5">On-chain / Decentralized Exchange</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-xl transition-colors">
                <X size={18} className="text-neutral-400" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${
                    i === step ? 'bg-purple-600/20 text-purple-400 border border-purple-600/40'
                    : i < step ? 'bg-green-600/10 text-green-500'
                    : 'text-neutral-600'}`}>
                    {i < step ? <Check size={10} /> : <span className="w-3 text-center">{i + 1}</span>}
                    {s}
                  </div>
                  {i < STEPS.length - 1 && <ChevronRight size={12} className="text-neutral-700 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-8" data-lenis-prevent>
            <AnimatePresence mode="wait">

              {/* ══ STEP 0: Token Search ══ */}
              {step === 0 && (
                <motion.div key="step0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">

                  {/* Search mode toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowCaInput(false); setSearchResults([]); setCaInput(''); }}
                      className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${!showCaInput ? 'bg-purple-600/20 border-purple-600/40 text-purple-400' : 'bg-neutral-900 border-neutral-800 text-neutral-500 hover:text-neutral-300'}`}
                    >
                      Search by Name/Ticker
                    </button>
                    <button
                      onClick={() => { setShowCaInput(true); setSearchResults([]); setQuery(''); }}
                      className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${showCaInput ? 'bg-purple-600/20 border-purple-600/40 text-purple-400' : 'bg-neutral-900 border-neutral-800 text-neutral-500 hover:text-neutral-300'}`}
                    >
                      Search by Contract Address (CA)
                    </button>
                  </div>

                  {/* Search input */}
                  {!showCaInput ? (
                    <div className="relative">
                      <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
                      <input
                        autoFocus
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="e.g. PEPE, WIF, SHIB..."
                        className="w-full bg-neutral-900 border border-neutral-700 rounded-2xl py-3.5 pl-11 pr-10 text-white font-bold focus:outline-none focus:border-purple-500/60 transition-all placeholder-neutral-600"
                      />
                      {searching && <Loader2 size={15} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 animate-spin" />}
                      {query && !searching && (
                        <button onClick={() => { setQuery(''); setSearchResults([]); }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-400">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          autoFocus
                          type="text"
                          value={caInput}
                          onChange={e => setCaInput(e.target.value)}
                          placeholder="0x... or base58 address"
                          className="w-full bg-neutral-900 border border-neutral-700 rounded-2xl py-3.5 px-4 text-white font-mono text-sm focus:outline-none focus:border-purple-500/60 transition-all placeholder-neutral-600"
                        />
                      </div>
                      <button
                        onClick={() => searchByCA(caInput)}
                        disabled={caInput.length < 10 || searching}
                        className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                      >
                        {searching ? <Loader2 size={15} className="animate-spin mx-auto" /> : 'Search by CA'}
                      </button>
                    </div>
                  )}

                  {searchError && (
                    <div className="flex items-center gap-2 text-red-400 text-xs">
                      <AlertCircle size={14} />
                      {searchError}
                    </div>
                  )}

                  {/* Results */}
                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      {/* Duplicate warning */}
                      {searchResults.some(p => p.isDuplicate) && (
                        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
                          <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-bold text-amber-400">Duplicate tokens detected</p>
                            <p className="text-xs text-neutral-500 mt-0.5">
                              Multiple tokens share the same ticker (e.g. fake vs real PEPE).
                              Check the contract address and liquidity before selecting.
                              Use "Search by CA" to find the exact token.
                            </p>
                          </div>
                        </div>
                      )}

                      <p className="text-[9px] font-black uppercase tracking-widest text-neutral-600">
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} from DexScreener
                      </p>

                      {searchResults.map(pair => (
                        <button
                          key={`${pair.chainId}-${pair.pairAddress}`}
                          onClick={() => setSelectedPair(pair)}
                          className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl border text-left transition-all ${
                            selectedPair?.pairAddress === pair.pairAddress
                              ? 'border-purple-600/60 bg-purple-600/10'
                              : 'border-neutral-800 bg-neutral-900/40 hover:border-neutral-700 hover:bg-neutral-900'
                          }`}
                        >
                          {/* Chain icon */}
                          <div className="w-9 h-9 rounded-xl bg-neutral-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {chainIcon(pair.chainId)
                              ? <img src={chainIcon(pair.chainId)} alt={pair.chainId} className="w-6 h-6 object-contain" />
                              : <span className="text-[10px] font-black text-neutral-400">{pair.chainId.slice(0, 3).toUpperCase()}</span>
                            }
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-black text-white">{pair.baseToken.symbol}</span>
                              <span className="text-xs text-neutral-500">/ {pair.quoteToken.symbol}</span>
                              {pair.isDuplicate && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-bold">DUPLICATE</span>
                              )}
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-500 font-bold">{chainLabel(pair.chainId)}</span>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-neutral-500">
                              <span>Price: <span className="text-neutral-300">${safeParseFloat(pair.priceUsd || '0').toFixed(6)}</span></span>
                              <span>Liq: <span className="text-neutral-300">{formatUsd(pair.liquidity?.usd)}</span></span>
                              <span>Vol 24h: <span className="text-neutral-300">{formatUsd(pair.volume?.h24)}</span></span>
                            </div>
                            {pair.isDuplicate && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-[9px] font-mono text-neutral-600 truncate">CA: {pair.baseToken.address.slice(0, 12)}...{pair.baseToken.address.slice(-6)}</span>
                                <button
                                  onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(pair.baseToken.address); }}
                                  className="text-neutral-600 hover:text-neutral-400"
                                >
                                  <Copy size={9} />
                                </button>
                              </div>
                            )}
                          </div>

                          {selectedPair?.pairAddress === pair.pairAddress && (
                            <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                              <Check size={11} className="text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Selected token chart preview */}
                  {selectedPair && chartUrl && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[9px] font-black uppercase tracking-widest text-neutral-600">Chart Preview — DexScreener</p>
                        <a href={selectedPair.url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 text-[10px] text-neutral-500 hover:text-purple-400 transition-colors">
                          Open full chart <ExternalLink size={10} />
                        </a>
                      </div>
                      <div className="rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-900">
                        <iframe
                          src={chartUrl}
                          className="w-full h-[280px]"
                          frameBorder="0"
                          title="DexScreener Chart"
                          allowFullScreen
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* ══ STEP 1: Trade Details ══ */}
              {step === 1 && selectedPair && (
                <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">

                  {/* Token info bar */}
                  <div className="flex items-center gap-3 bg-neutral-900/60 border border-neutral-800 rounded-2xl px-4 py-3">
                    <div className="w-8 h-8 rounded-xl bg-neutral-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {chainIcon(selectedPair.chainId)
                        ? <img src={chainIcon(selectedPair.chainId)} alt={selectedPair.chainId} className="w-5 h-5 object-contain" />
                        : <span className="text-[9px] font-black text-neutral-400">{selectedPair.chainId.slice(0, 3).toUpperCase()}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-white">{selectedPair.baseToken.symbol}<span className="text-neutral-500 font-normal">/{selectedPair.quoteToken.symbol}</span></p>
                      <p className="text-[10px] text-neutral-500">{chainLabel(selectedPair.chainId)} · ${safeParseFloat(selectedPair.priceUsd || '0').toFixed(6)}</p>
                    </div>
                    <a href={selectedPair.url} target="_blank" rel="noreferrer" className="text-neutral-600 hover:text-purple-400 transition-colors">
                      <ExternalLink size={14} />
                    </a>
                  </div>

                  {/* Wallet selector */}
                  {walletAccounts.length > 0 && (
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Wallet (Optional)</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setWalletAccountId(null)}
                          className={`px-3 py-2.5 rounded-xl border text-xs font-bold text-left transition-all ${walletAccountId === null ? 'border-purple-600/50 bg-purple-600/10 text-purple-400' : 'border-neutral-800 bg-neutral-900 text-neutral-500 hover:border-neutral-700'}`}
                        >
                          No specific wallet
                        </button>
                        {walletAccounts.map(w => (
                          <button
                            key={w.id}
                            onClick={() => setWalletAccountId(w.id)}
                            className={`px-3 py-2.5 rounded-xl border text-xs font-bold text-left transition-all ${walletAccountId === w.id ? 'border-purple-600/50 bg-purple-600/10 text-purple-400' : 'border-neutral-800 bg-neutral-900 text-neutral-500 hover:border-neutral-700'}`}
                          >
                            <p className="text-white font-bold truncate">{w.network_display_name}</p>
                            <p className="text-neutral-600 font-mono text-[9px]">{w.wallet_address.slice(0, 8)}…</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Buy entry */}
                  <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-5 space-y-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-green-500">Entry (Buy)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Date & Time</label>
                        <input type="datetime-local" value={buyDate} onChange={e => setBuyDate(e.target.value)}
                          className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500/60" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Buy Price (USD)</label>
                        <input type="number" step="any" value={buyPrice} onChange={e => setBuyPrice(e.target.value)}
                          placeholder={selectedPair.priceUsd || '0.00'}
                          className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-purple-500/60" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Quantity (Tokens)</label>
                        <input type="number" step="any" value={qty} onChange={e => setQty(e.target.value)}
                          placeholder="0.00"
                          className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-purple-500/60" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Gas / Fee (USD)</label>
                        <input type="number" step="any" value={fee} onChange={e => setFee(e.target.value)}
                          placeholder="0.00"
                          className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-purple-500/60" />
                      </div>
                    </div>

                    {/* Buy value preview */}
                    {buyPrice && qty && (
                      <div className="bg-neutral-800/50 rounded-xl px-4 py-2.5 flex justify-between">
                        <span className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">Position Value</span>
                        <span className="text-sm font-black font-mono text-white">${(safeParseFloat(buyPrice) * safeParseFloat(qty) || 0).toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Sell / Exit toggle */}
                  <div>
                    <button
                      onClick={() => setShowSell(!showSell)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all ${showSell ? 'border-red-600/40 bg-red-600/10' : 'border-neutral-800 bg-neutral-900/40 hover:border-neutral-700'}`}
                    >
                      <span className={`text-xs font-black uppercase tracking-widest ${showSell ? 'text-red-400' : 'text-neutral-500'}`}>
                        {showSell ? '✓ Exit (Sell) Added' : '+ Add Exit (Sell)'}
                      </span>
                      <span className="text-[10px] text-neutral-600">{showSell ? 'Click to remove' : 'Optional — leave open if not closed yet'}</span>
                    </button>

                    {showSell && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-5 space-y-4 mt-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-red-400">Exit (Sell)</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Date & Time</label>
                            <input type="datetime-local" value={sellDate} onChange={e => setSellDate(e.target.value)}
                              className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-red-500/60" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Sell Price (USD)</label>
                            <input type="number" step="any" value={sellPrice} onChange={e => setSellPrice(e.target.value)}
                              placeholder="0.00"
                              className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-red-500/60" />
                          </div>
                        </div>
                        {buyPrice && sellPrice && qty && (
                          <div className={`rounded-xl px-4 py-2.5 flex justify-between ${pnl.net >= 0 ? 'bg-green-600/10' : 'bg-red-600/10'}`}>
                            <span className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">Est. P&L</span>
                            <span className={`text-sm font-black font-mono ${pnl.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {pnl.net >= 0 ? '+' : ''}{pnl.net.toFixed(4)} USD
                            </span>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ══ STEP 2: Analysis ══ */}
              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Optional — fill what's relevant</p>

                  {/* Primary Setup */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Setup Type</label>
                    <div className="flex flex-wrap gap-2">
                      {SETUP_CHOICES.map(s => (
                        <button key={s} onClick={() => toggleOption('primary_setup_type', s)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isSelected('primary_setup_type', s) ? 'bg-purple-600 border-purple-600 text-white' : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Key Indicators */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Key Indicators</label>
                    <div className="flex flex-wrap gap-2">
                      {INDICATOR_CHOICES.map(s => (
                        <button key={s} onClick={() => toggleOption('key_indicators', s)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isSelected('key_indicators', s) ? 'bg-purple-600 border-purple-600 text-white' : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Timeframe */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Timeframe</label>
                    <div className="flex flex-wrap gap-2">
                      {TIMEFRAME_CHOICES.map(s => (
                        <button key={s} onClick={() => toggleOption('timeframe_analysis', s)}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isSelected('timeframe_analysis', s) ? 'bg-purple-600 border-purple-600 text-white' : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sliders */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Confidence ({analysis.pre_trade_confidence}/10)</label>
                      <input type="range" min="1" max="10" value={analysis.pre_trade_confidence}
                        onChange={e => setAnalysis(p => ({ ...p, pre_trade_confidence: parseInt(e.target.value) }))}
                        className="w-full h-1 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-purple-600" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Emotional Load ({analysis.emotional_load}/10)</label>
                      <input type="range" min="1" max="10" value={analysis.emotional_load}
                        onChange={e => setAnalysis(p => ({ ...p, emotional_load: parseInt(e.target.value) }))}
                        className="w-full h-1 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-purple-600" />
                    </div>
                  </div>

                  {/* Risk % */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Risk %</label>
                    <input type="number" step="any" value={analysis.risk_percentage}
                      onChange={e => setAnalysis(p => ({ ...p, risk_percentage: e.target.value }))}
                      placeholder="e.g. 2.5"
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-purple-500/60" />
                  </div>

                  {/* Remarks */}
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Remarks</label>
                    <textarea value={analysis.remarks} onChange={e => setAnalysis(p => ({ ...p, remarks: e.target.value }))}
                      placeholder="Why this trade? Thesis, on-chain signals..."
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2.5 text-xs text-neutral-300 resize-none h-20 outline-none focus:border-neutral-600" />
                  </div>
                </motion.div>
              )}

              {/* ══ STEP 3: Review ══ */}
              {step === 3 && selectedPair && (
                <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                  <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                    <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-4">Summary</p>
                    <div className="flex items-end justify-between mb-5">
                      <div>
                        <p className="text-3xl font-black text-white tracking-tight">
                          {selectedPair.baseToken.symbol}<span className="text-neutral-500 text-xl">/{selectedPair.quoteToken.symbol}</span>
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">{chainLabel(selectedPair.chainId)} · DEX: {selectedPair.dexId}</p>
                      </div>
                      <div className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider ${
                        pnl.status === 'WIN' ? 'bg-green-600/20 text-green-400 border border-green-600/40'
                        : pnl.status === 'LOSS' ? 'bg-red-600/20 text-red-400 border border-red-600/40'
                        : 'bg-neutral-800 text-neutral-400 border border-neutral-700'}`}>
                        {pnl.status}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Net P&L', value: showSell ? `${pnl.net >= 0 ? '+' : ''}${pnl.net.toFixed(4)}` : '—', color: pnl.net >= 0 ? 'text-green-400' : 'text-red-400' },
                        { label: 'Buy Value', value: `$${pnl.buyValue.toFixed(2)}`, color: 'text-white' },
                        { label: 'Gas Fees', value: `$${pnl.fees.toFixed(4)}`, color: 'text-yellow-400' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="bg-neutral-800/50 rounded-xl p-3">
                          <p className="text-[9px] font-black uppercase tracking-widest text-neutral-500 mb-1">{label}</p>
                          <p className={`text-base font-black font-mono ${color}`}>{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CA info */}
                  <div className="bg-neutral-900/20 border border-neutral-800 rounded-xl px-4 py-3 space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-neutral-600">Contract Address</p>
                    <p className="text-xs font-mono text-neutral-400 break-all">{selectedPair.baseToken.address}</p>
                  </div>

                  {analysis.remarks && (
                    <div className="bg-neutral-900/20 border border-neutral-800 rounded-xl px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-neutral-600 mb-1">Remarks</p>
                      <p className="text-xs text-neutral-400">{analysis.remarks}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center gap-2 bg-red-900/20 border border-red-900/50 rounded-xl px-4 py-3">
                <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-5 border-t border-neutral-800/60 bg-[#0A0A0A] flex justify-between items-center">
            <button onClick={step === 0 ? onClose : () => setStep(s => s - 1)}
              className="flex items-center gap-2 px-5 py-2.5 bg-neutral-900 border border-neutral-800 rounded-xl text-sm font-bold text-neutral-400 hover:text-white hover:bg-neutral-800 transition-all">
              <ChevronLeft size={16} /> {step === 0 ? 'Cancel' : 'Back'}
            </button>
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                {STEPS.map((_, i) => (
                  <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-purple-500' : i < step ? 'w-3 bg-green-500' : 'w-3 bg-neutral-700'}`} />
                ))}
              </div>
              {step < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={(step === 0 && !canNext0) || (step === 1 && !canNext1)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-xl text-sm font-black text-white transition-all shadow-lg shadow-purple-900/30 disabled:opacity-40 disabled:cursor-not-allowed">
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-500 rounded-xl text-sm font-black text-white transition-all shadow-lg shadow-green-900/30 disabled:opacity-50">
                  {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><Check size={16} /> Save DEX Trade</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default AddDexTradeWizard;
